import { useEffect, useState } from 'react';
import { createOauthClient, fetchOauthClients, newOauthClientSecret, revokeOauthClient, updateOauthClient, type CreatedOauthClient, type OauthClientList, type OauthClientView } from '../lib/api';
import { useActionError } from '../lib/action-error';
import { ask } from '../lib/ask';
import { formatShortDate } from '../lib/format-date';
import { labels } from '../lib/labels';

const MAX_TOKEN_MINUTES = 1440;

/** A secret that the card shows once: after a create, or after a new secret. */
type Revealed = CreatedOauthClient & { kind: 'created' | 'secret' };

type LoadState = { status: 'loading' } | { status: 'error' } | ({ status: 'ok' } & OauthClientList);

/** The "API clients" section of `#settings` (ADR 0043): the Keycloak clients a machine caller
 *  uses to read `/api/v1/*`. The secret lives only in this component's state, right after the
 *  create, so a reload never shows it again. */
export function ApiClients() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [created, setCreated] = useState<Revealed | null>(null);
  const action = useActionError();

  function reload() {
    fetchOauthClients()
      .then((list) => setState({ status: 'ok', ...list }))
      .catch(() => setState({ status: 'error' }));
  }

  useEffect(reload, []);

  async function handleCreate() {
    const answers = await ask({
      ...labels.apiClients.createConfirm,
      fields: [{ name: 'name', label: labels.apiClients.nameLabel, required: true }],
      confirmLabel: labels.apiClients.createButton,
      tone: 'primary',
    });
    if (!answers) return;
    let result: CreatedOauthClient | undefined;
    if (!(await action.run(() => readable(async () => (result = await createOauthClient(answers.name)))))) return;
    setCreated({ ...result!, kind: 'created' });
    reload();
  }

  async function handleNewSecret(client: OauthClientView) {
    const answers = await ask({
      ...labels.apiClients.newSecretConfirm(client.name, client.id),
      confirmLabel: labels.apiClients.newSecretButton,
      tone: 'guard',
    });
    if (!answers) return;
    let result: CreatedOauthClient | undefined;
    if (!(await action.run(() => readable(async () => (result = await newOauthClientSecret(client.id)))))) return;
    setCreated({ ...result!, kind: 'secret' });
  }

  async function handleMinutes(client: OauthClientView) {
    const answers = await ask({
      ...labels.apiClients.minutesConfirm(client.name, client.id),
      fields: [
        {
          name: 'minutes',
          label: labels.apiClients.minutesLabel,
          kind: 'number',
          required: true,
          value: client.tokenMinutes === null ? '' : String(client.tokenMinutes),
        },
      ],
      confirmLabel: labels.apiClients.minutesButton,
      tone: 'guard',
    });
    if (!answers) return;
    const minutes = Number(answers.minutes);
    const ok = await action.run(() =>
      readable(async () => {
        if (!Number.isInteger(minutes) || minutes < 1 || minutes > MAX_TOKEN_MINUTES) {
          throw new Error(labels.apiClients.minutesInvalid);
        }
        await updateOauthClient(client.id, { tokenMinutes: minutes });
      }),
    );
    if (ok) reload();
  }

  async function handleEnabled(client: OauthClientView) {
    const enable = !client.enabled;
    const words = enable
      ? { confirm: labels.apiClients.enableConfirm, button: labels.apiClients.enableButton, tone: 'guard' as const }
      : { confirm: labels.apiClients.disableConfirm, button: labels.apiClients.disableButton, tone: 'danger' as const };
    const answers = await ask({ ...words.confirm(client.name, client.id), confirmLabel: words.button, tone: words.tone });
    if (!answers) return;
    if (await action.run(() => readable(() => updateOauthClient(client.id, { enabled: enable })))) reload();
  }

  async function handleRevoke(client: OauthClientView) {
    const answers = await ask({ ...labels.apiClients.revokeConfirm(client.name, client.id), confirmLabel: labels.apiClients.revokeButton, tone: 'danger' });
    if (!answers) return;
    if (!(await action.run(() => readable(() => revokeOauthClient(client.id))))) return;
    if (created?.id === client.id) setCreated(null);
    reload();
  }

  return (
    <section className="panel flow-md">
      <div className="panel__head">
        <h3>{labels.apiClients.heading}</h3>
        <button type="button" className="btn" onClick={handleCreate}>
          {labels.apiClients.createButton}
        </button>
      </div>
      {state.status === 'ok' && (
        <p className="panel__sub">
          {labels.apiClients.help.before}
          <code>{state.tokenUrl}</code>
          {labels.apiClients.help.after}
        </p>
      )}
      <p className="panel__sub">
        {labels.apiClients.effects} {labels.apiClients.superAdmin}
      </p>

      {created && (
        <div className="notice notice--warn secret-reveal">
          <p role="status">{(created.kind === 'created' ? labels.apiClients.revealed : labels.apiClients.revealedSecret)(created.name)}</p>
          <CopyRow key={created.id} label={labels.apiClients.clientIdLabel} value={created.id} />
          <CopyRow key={created.secret} label={labels.apiClients.secretLabel} value={created.secret} />
          <p>
            <strong>{labels.apiClients.copySecretNow}</strong>
          </p>
          <p>
            <button type="button" className="btn btn--text" onClick={() => setCreated(null)}>
              {labels.apiClients.dismiss}
            </button>
          </p>
        </div>
      )}
      {action.notice}

      {state.status === 'loading' && <p className="notice">{labels.apiClients.loading}</p>}
      {state.status === 'error' && (
        <p className="notice notice--crit" role="alert">
          {labels.apiClients.loadFailed}
        </p>
      )}
      {state.status === 'ok' && state.clients.length === 0 && <p className="notice">{labels.apiClients.empty}</p>}
      {state.status === 'ok' && state.clients.length > 0 && (
        <div className="responsive-table api-clients-table">
          <table>
            <thead>
              <tr>
                <th>{labels.apiClients.columns.name}</th>
                <th>{labels.apiClients.columns.id}</th>
                <th>{labels.apiClients.columns.createdAt}</th>
                <th>{labels.apiClients.columns.createdBy}</th>
                <th>{labels.apiClients.columns.tokenMinutes}</th>
                <th>{labels.apiClients.columns.status}</th>
                <th className="table-actions"><span className="visually-hidden">{labels.tableActions}</span></th>
              </tr>
            </thead>
            <tbody>
              {state.clients.map((client) => (
                <tr key={client.id}>
                  <td>{client.name}</td>
                  <td className="mono" data-label={labels.apiClients.columns.id}>
                    {client.id}
                  </td>
                  <td className="mono" data-label={labels.apiClients.columns.createdAt}>
                    {client.createdAt ? formatShortDate(client.createdAt) : labels.apiClients.noValue}
                  </td>
                  <td data-label={labels.apiClients.columns.createdBy}>{client.createdBy ?? labels.apiClients.noValue}</td>
                  <td data-label={labels.apiClients.columns.tokenMinutes}>
                    {client.tokenMinutes === null ? labels.apiClients.tokenMinutesUnknown : labels.apiClients.tokenMinutes(client.tokenMinutes)}
                  </td>
                  <td data-label={labels.apiClients.columns.status}>
                    <ClientStatus client={client} />
                  </td>
                  <td className="table-actions">
                    <div className="api-client-actions">
                      <button
                        type="button"
                        className="btn btn--text"
                        aria-label={`${labels.apiClients.newSecretButton} ${client.name}`}
                        onClick={() => handleNewSecret(client)}
                      >
                        {labels.apiClients.newSecretButton}
                      </button>
                      <button
                        type="button"
                        className="btn btn--text"
                        aria-label={`${labels.apiClients.minutesButton} ${client.name}`}
                        onClick={() => handleMinutes(client)}
                      >
                        {labels.apiClients.minutesButton}
                      </button>
                      <button
                        type="button"
                        className="btn btn--text"
                        aria-label={`${client.enabled ? labels.apiClients.disableButton : labels.apiClients.enableButton} ${client.name}`}
                        onClick={() => handleEnabled(client)}
                      >
                        {client.enabled ? labels.apiClients.disableButton : labels.apiClients.enableButton}
                      </button>
                      <button
                        type="button"
                        className="btn btn--text btn--danger-text"
                        aria-label={`${labels.apiClients.revokeButton} ${client.name}`}
                        onClick={() => handleRevoke(client)}
                      >
                        {labels.apiClients.revokeButton}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** A status word is a pill with the word in it, so colour is never the only signal. A client can be
 *  both disabled and without API access; then both pills show. */
function ClientStatus({ client }: { client: OauthClientView }) {
  if (client.enabled && client.apiAccess) return <>{labels.apiClients.active}</>;
  return (
    <>
      {!client.enabled && <span className="pill">{labels.apiClients.disabled}</span>}
      {!client.enabled && !client.apiAccess && ' '}
      {!client.apiAccess && <span className="pill verdict" data-status="fail">{labels.apiClients.noApiAccess}</span>}
    </>
  );
}

/** A failed write shows the backend's own sentence. A dropped connection or a bare proxy error has
 *  none (`Failed to fetch`, `POST /api/... -> 502`), so it shows one plain sentence instead. */
async function readable<T>(write: () => Promise<T>): Promise<T> {
  try {
    return await write();
  } catch (error) {
    const bare = !(error instanceof Error) || error instanceof TypeError || /^(POST|PATCH|DELETE) \//.test(error.message);
    throw bare ? new Error(labels.apiClients.unreachable) : error;
  }
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopy('copied');
    } catch {
      setCopy('failed');
    }
  }
  return (
    <div className="secret-reveal__row">
      <span className="secret-reveal__label">{label}</span>
      <code className="mono secret-reveal__value">{value}</code>
      <button
        type="button"
        className="btn"
        aria-label={`${labels.apiClients.copy} ${label}`}
        onClick={() => void handleCopy()}
      >
        {copy === 'copied' ? labels.apiClients.copied : labels.apiClients.copy}
      </button>
      {copy === 'failed' && (
        <span className="secret-reveal__failed" role="alert">
          {labels.apiClients.copyFailed}
        </span>
      )}
    </div>
  );
}
